import React from 'react';
import { Header } from '@amzn/awsui-components-react-v3/polaris';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import ListTable, { ListColumn } from '../../Common/ListTable';
import { convertDateTimePretty, valueOrDash } from '../../../utils/ReadableConverter';
// eslint-disable-next-line no-unused-vars
import { ChangeSetSummary, formatLabel } from './pricingModels';

/**
 * When and by whom the change set was approved or rejected, e.g. "October 16, 2026 8:00 AM UTC by
 * approver"; a dash until then.
 */
const reviewText = ({ reviewedAt, reviewedBy }: ChangeSetSummary): string => {
  if (!reviewedAt) {
    return '-';
  }
  const reviewed = convertDateTimePretty(reviewedAt);
  return reviewedBy ? `${reviewed} by ${reviewedBy}` : reviewed;
};

const COLUMNS: ListColumn<ChangeSetSummary>[] = [
  {
    id: 'createdAt',
    header: 'Created',
    cell: (changeSet): string => convertDateTimePretty(changeSet.createdAt),
  },
  {
    id: 'author',
    header: 'Author',
    cell: (changeSet): string => changeSet.author,
  },
  {
    id: 'type',
    header: 'Type',
    cell: (changeSet): string => formatLabel(changeSet.type),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (changeSet): string => formatLabel(changeSet.status),
  },
  {
    id: 'reviewed',
    header: 'Reviewed',
    cell: reviewText,
  },
  {
    id: 'reviewComment',
    header: 'Comment',
    cell: (changeSet): string => valueOrDash(changeSet.reviewComment),
  },
];

const filterText = (changeSet: ChangeSetSummary): string =>
  [
    changeSet.author,
    formatLabel(changeSet.type),
    formatLabel(changeSet.status),
    changeSet.reviewComment || '',
  ].join(' ');

/** A model's change sets, in the order given (newest first). Used by the pricing and scoping model pages. */
const PricingModelChangeSets = ({
  id = 'pricingModelChangeSets',
  changeSets,
}: {
  id?: string;
  changeSets: ChangeSetSummary[];
}): JSX.Element => {
  return (
    <ListTable
      id={id}
      items={changeSets}
      columns={COLUMNS}
      filterText={filterText}
      filteringPlaceholder="Find change sets"
      trackBy="changeSetId"
      header={
        <Header variant="h2" counter={`(${changeSets.length})`}>
          Change sets
        </Header>
      }
      empty="No change sets."
    />
  );
};

export default PricingModelChangeSets;
